import React from 'react';
import { cn } from '@ais-ui/utils';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { X } from 'lucide-react';
import { PiMagnifyingGlassPlusThin } from 'react-icons/pi';

/**
 * Composable LayerRow — the single source of truth for layer row layout.
 * Both flat layers and feature collection parents use this to guarantee
 * identical padding, alignment, and slot positions. Prevents visual drift.
 *
 * Usage:
 *   <LayerRow className="...">
 *     <LayerRow.Leading>
 *       <Checkbox ... />
 *       <Icon ... />
 *       <Label ... />
 *     </LayerRow.Leading>
 *     <LayerRow.Trailing>
 *       <LayerRow.RemoveButton onClick={...} />
 *     </LayerRow.Trailing>
 *   </LayerRow>
 */

interface LayerRowProps {
  children: React.ReactNode;
  className?: string;
  'data-parent-row'?: boolean;
}

function LayerRowRoot({ children, className, ...rest }: LayerRowProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-2 transition-all duration-200',
        'bg-card hover:bg-muted rounded-lg hover:shadow-sm',
        className,
      )}
      {...rest}
    >
      <div className="w-full h-10 flex items-center justify-between relative">
        {children}
      </div>
    </div>
  );
}

interface SlotProps {
  children: React.ReactNode;
  className?: string;
}

function Leading({ children, className }: SlotProps) {
  return (
    <div className={cn('flex items-center space-x-4 min-w-0', className)}>
      {children}
    </div>
  );
}

function Trailing({ children, className }: SlotProps) {
  return (
    <div className={cn('flex space-x-1 flex-shrink-0', className)}>
      {children}
    </div>
  );
}

interface RemoveButtonProps {
  onClick: (e: React.MouseEvent) => void;
  tooltip?: string;
  id?: string;
}

function RemoveButton({
  onClick,
  tooltip = 'Remove layer',
  id,
}: RemoveButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={id}
          size="icon"
          variant="ghost"
          className="h-9 w-9 flex-shrink-0 p-0 m-0"
          onClick={onClick}
        >
          <X className="size-5 opacity-50" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

interface ZoomButtonProps {
  onClick: (e: React.MouseEvent) => void;
  tooltip?: string;
}

function ZoomButton({ onClick, tooltip = 'Zoom to layer' }: ZoomButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-9 w-9 flex-shrink-0 p-0 m-0"
          onClick={onClick}
        >
          <PiMagnifyingGlassPlusThin className="size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export const LayerRow = Object.assign(LayerRowRoot, {
  Leading,
  Trailing,
  RemoveButton,
  ZoomButton,
});
